import React, { useCallback, useEffect, useRef, useState } from "react";
import "../affidea-day/affidea-day.css";
import "./HealthRushGame.css";
import { isTabletWidthOnly as isTablet } from "../shared/device";
import { Entry, loadEntries, rankEntries, saveEntries } from "../affidea-day/ranking";
import EventHeader from "../affidea-day/EventHeader";
import NameRegistration from "../affidea-day/NameRegistration";
import Podium from "../affidea-day/Podium";
import AdminModal from "../affidea-day/AdminModal";

// The falling-items engine (canvas, sprite cache, rAF loop, hit test, sounds) is copied
// from EscolheBemGame.tsx on purpose, so the tuned Continente game stays untouched.
// Its perf notes are kept with the code they explain.

type Phase = "register" | "countdown" | "playing" | "finished";

interface HealthRushGameProps {
  onBackToHome: () => void;
}

interface FallingItem {
  id: number;
  imagePath: string;
  points: number;
  xPercent: number;
  y: number;
  speed: number;
  // perf: cache the scaled sprite directly on the item so drawCanvasFrame skips the per-draw Map lookup; nulled on resize
  scaledSprite: HTMLCanvasElement | null;
}

const RANKING_KEY = "health-rush-ranking";
const SPRITES = "/images/affidea-day/health-rush";
// Each spawn picks a group 50/50, then an image within it.
const GROUPS = [
  { points: 10, images: ["mri", "ct", "mamografo"].map((n) => `${SPRITES}/${n}.webp`) },
  // Only the sharp virus falls; the designers' other viruses are out of focus (depth decoration).
  { points: -10, images: [`${SPRITES}/virus.webp`] },
];
const ALL_SPRITES = GROUPS.flatMap((group) => group.images);

const ROUND_SECONDS = 20;
const COUNTDOWN_FROM = 3;
// How long "Fim do tempo!" stays up before the podium, so frantic last taps don't hit Recomeçar.
const END_PAUSE_MS = 1500;
const ITEMS_PER_SECOND = 2;
const FINAL_RUSH_SECONDS = 7;
const FINAL_RUSH_MULTIPLIER = 1.35;
const ITEM_SPEED = 290;
const POINTS_FEEDBACK_DURATION_MS = 1200;
const MAX_ACTIVE_ITEMS = 24;
const MAX_ACTIVE_ITEMS_MIN = 12;
const MAX_SPAWNS_PER_FRAME = 3;
const REFERENCE_ARENA_AREA = 1024 * 576;
const MAX_DELTA_MS = 40;
// Render at the screen's real density (Escolhe Bem caps this at 1, which looks blurry on
// retina tablets); 2 keeps the canvas cheap on very dense screens.
const MAX_CANVAS_PIXEL_RATIO = 2;
const ITEM_SIZE_MIN = 74;
const ITEM_SIZE_MAX = 170;
// Item size as a share of the arena width; a bit bigger than Escolhe Bem's 0.115 since a 3:2
// machine only fills ~2/3 of its square cell (0.18 was too big).
const ITEM_SIZE_RATIO = 0.14;

const clampValue = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const getRandomFrom = <T,>(items: T[]) => items[Math.floor(Math.random() * items.length)];

function getDynamicMaxActiveItems(arenaWidth: number, arenaHeight: number) {
  const areaScale = (arenaWidth * arenaHeight) / REFERENCE_ARENA_AREA;
  return clampValue(Math.round(MAX_ACTIVE_ITEMS * areaScale), MAX_ACTIVE_ITEMS_MIN, MAX_ACTIVE_ITEMS);
}

export default function HealthRushGame({ onBackToHome }: HealthRushGameProps) {
  const arenaRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<number | null>(null);
  const spawnAccumulatorRef = useRef(0);
  const itemIdRef = useRef(0);
  const activeItemsRef = useRef<Map<number, FallingItem>>(new Map());
  // perf: pin decoded pixels in an ImageBitmap so Firefox can't evict and re-decode on the main thread
  const spriteCacheRef = useRef<Map<string, ImageBitmap>>(new Map());
  const scaledSpriteCacheRef = useRef<Map<string, HTMLCanvasElement>>(new Map());
  const pendingPointerRef = useRef<{ x: number; y: number } | null>(null);
  const removedItemBufferRef = useRef<number[]>([]);
  const arenaMetricsRef = useRef({ width: 1, height: 1, itemSize: ITEM_SIZE_MIN, pixelRatio: 1 });
  // perf: track prior item size so we only clear scaled sprite cache when size actually changes
  const prevItemSizeRef = useRef(-1);
  // perf: cache the 2D context so drawCanvasFrame doesn't re-query it every frame
  const canvasContextRef = useRef<CanvasRenderingContext2D | null>(null);
  // perf: cache arena bounds so pointerdown doesn't call getBoundingClientRect (potential forced reflow) on every tap
  const arenaBoundsRef = useRef<DOMRect | null>(null);
  const scoreRef = useRef(0);
  const timeLeftRef = useRef(ROUND_SECONDS);
  const audioContextRef = useRef<AudioContext | null>(null);
  const feedbackTimerRef = useRef<number | null>(null);
  const feedbackSoundTimeoutsRef = useRef<number[]>([]);
  const endTimeoutRef = useRef<number | null>(null);

  const [phase, setPhase] = useState<Phase>("register");
  const [entries, setEntries] = useState<Entry[]>(() => loadEntries(RANKING_KEY));
  const [name, setName] = useState("");
  const [countdown, setCountdown] = useState(COUNTDOWN_FROM);
  const [timeLeft, setTimeLeft] = useState(ROUND_SECONDS);
  const [score, setScore] = useState(0);
  const [lastPoints, setLastPoints] = useState<number | null>(null);
  const [pointsFlashKey, setPointsFlashKey] = useState(0);
  const [roundOver, setRoundOver] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);

  const isRunning = phase === "playing" && !roundOver;
  const arenaVisible = phase === "countdown" || phase === "playing";

  const getOrCreateAudioContext = useCallback(() => {
    if (audioContextRef.current) return audioContextRef.current;
    if (typeof window.AudioContext === "undefined") return null;
    audioContextRef.current = new window.AudioContext();
    return audioContextRef.current;
  }, []);

  const playTone = useCallback(
    (frequency: number, durationMs: number, oscillatorType: OscillatorType, volume = 0.15) => {
      const audioContext = getOrCreateAudioContext();
      if (!audioContext) return;

      const startTone = () => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        const now = audioContext.currentTime;
        oscillator.type = oscillatorType;
        oscillator.frequency.setValueAtTime(frequency, now);
        gainNode.gain.setValueAtTime(0.0001, now);
        gainNode.gain.exponentialRampToValueAtTime(volume, now + 0.012);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start(now);
        oscillator.stop(now + durationMs / 1000 + 0.02);
      };

      if (audioContext.state === "suspended") {
        // perf: some browsers throw synchronously from resume(); guard both throw and rejected-promise paths
        try {
          void audioContext
            .resume()
            .then(startTone)
            .catch(() => undefined);
        } catch {
          // silently swallow audio errors
        }
        return;
      }
      startTone();
    },
    [getOrCreateAudioContext],
  );

  const playPointsFeedbackSound = useCallback(
    (points: number) => {
      const [first, second] =
        points > 0
          ? [() => playTone(900, 90, "triangle", 0.14), () => playTone(1200, 110, "triangle", 0.14)]
          : [() => playTone(260, 120, "sawtooth", 0.13), () => playTone(170, 150, "sawtooth", 0.13)];
      first();
      feedbackSoundTimeoutsRef.current.push(window.setTimeout(second, points > 0 ? 85 : 95));
    },
    [playTone],
  );

  const syncCanvasSize = useCallback(() => {
    const canvasElement = canvasRef.current;
    const arenaElement = arenaRef.current;
    if (!canvasElement || !arenaElement) return;

    const width = Math.max(1, arenaElement.clientWidth);
    const height = Math.max(1, arenaElement.clientHeight);
    const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_CANVAS_PIXEL_RATIO);
    // Item size in whole device pixels, so each pre-scaled sprite is drawn 1:1 (sharp).
    const itemSize =
      Math.round(clampValue(width * ITEM_SIZE_RATIO, ITEM_SIZE_MIN, ITEM_SIZE_MAX) * pixelRatio) /
      pixelRatio;
    arenaMetricsRef.current = { width, height, itemSize, pixelRatio };

    // perf: only re-rasterize sprites when the device-pixel item size actually changed
    const spritePx = Math.round(itemSize * pixelRatio);
    if (spritePx !== prevItemSizeRef.current) {
      scaledSpriteCacheRef.current.clear();
      activeItemsRef.current.forEach((item) => {
        item.scaledSprite = null;
      });
      prevItemSizeRef.current = spritePx;
    }

    canvasElement.style.width = `${width}px`;
    canvasElement.style.height = `${height}px`;
    canvasElement.width = Math.floor(width * pixelRatio);
    canvasElement.height = Math.floor(height * pixelRatio);

    const context = canvasElement.getContext("2d");
    if (!context) return;
    canvasContextRef.current = context;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    // perf: sprites are pre-rasterized at target size and drawn at integer positions 1:1, so smoothing is pure overhead
    context.imageSmoothingEnabled = false;

    // perf: refresh cached arena bounds whenever layout changes
    arenaBoundsRef.current = arenaElement.getBoundingClientRect();
  }, []);

  // spritePx is the item size in device pixels; downscaled once, with high quality, then reused.
  const getScaledSprite = useCallback((imagePath: string, spritePx: number) => {
    const sprite = spriteCacheRef.current.get(imagePath);
    if (!sprite) return null;

    const cacheKey = `${imagePath}@${spritePx}`;
    const cached = scaledSpriteCacheRef.current.get(cacheKey);
    if (cached) return cached;

    const scaledCanvas = document.createElement("canvas");
    scaledCanvas.width = spritePx;
    scaledCanvas.height = spritePx;
    const scaledContext = scaledCanvas.getContext("2d");
    if (!scaledContext) return null;
    scaledContext.imageSmoothingEnabled = true;
    scaledContext.imageSmoothingQuality = "high";
    scaledContext.drawImage(sprite, 0, 0, spritePx, spritePx);

    scaledSpriteCacheRef.current.set(cacheKey, scaledCanvas);
    return scaledCanvas;
  }, []);

  const drawCanvasFrame = useCallback(() => {
    const canvasElement = canvasRef.current;
    if (!canvasElement) return;
    let context = canvasContextRef.current;
    if (!context || context.canvas !== canvasElement) {
      // The arena (and its canvas) remounts every round, so the cached context can be stale.
      context = canvasElement.getContext("2d");
      if (!context) return;
      canvasContextRef.current = context;
    }

    const ctx = context;
    const { width, height, itemSize, pixelRatio } = arenaMetricsRef.current;
    ctx.clearRect(0, 0, width, height);
    const spritePx = Math.round(itemSize * pixelRatio);
    const halfSize = itemSize / 2;

    // perf: Map.forEach instead of Array.from(values()) avoids a per-frame array copy
    activeItemsRef.current.forEach((item) => {
      let scaledSprite = item.scaledSprite;
      if (!scaledSprite) {
        scaledSprite = getScaledSprite(item.imagePath, spritePx);
        if (!scaledSprite) return;
        item.scaledSprite = scaledSprite;
      }
      const xPx = (item.xPercent / 100) * width - halfSize;
      // Snap to device pixels (not CSS pixels): sprites stay 1:1 and sharp, and motion moves in
      // even half-pixel steps on retina screens instead of uneven whole-pixel jumps.
      ctx.drawImage(
        scaledSprite,
        Math.round(xPx * pixelRatio) / pixelRatio,
        Math.round(item.y * pixelRatio) / pixelRatio,
        itemSize,
        itemSize,
      );
    });
  }, [getScaledSprite]);

  const buildSpawnItem = useCallback((): FallingItem | null => {
    const group = getRandomFrom(GROUPS);
    const loaded = group.images.filter((path) => spriteCacheRef.current.has(path));
    if (loaded.length === 0) return null;
    return {
      id: itemIdRef.current++,
      imagePath: getRandomFrom(loaded),
      points: group.points,
      xPercent: 8 + Math.random() * 84,
      y: -110,
      speed: ITEM_SPEED,
      scaledSprite: null,
    };
  }, []);

  // Decode every sprite once up front; spawns only use sprites that finished loading.
  useEffect(() => {
    let cancelled = false;
    const spriteCache = spriteCacheRef.current;
    ALL_SPRITES.forEach((path) => {
      const image = new Image();
      image.onload = async () => {
        try {
          // perf: createImageBitmap decodes once and pins the result, so later drawImage calls never re-decode
          const bitmap = await createImageBitmap(image);
          if (cancelled) return bitmap.close();
          spriteCache.set(path, bitmap);
        } catch {
          // decode failed: the sprite stays unloaded and is never spawned
        }
      };
      image.src = path;
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const clearActiveItems = useCallback(() => {
    activeItemsRef.current.clear();
    drawCanvasFrame();
  }, [drawCanvasFrame]);

  const handleItemHit = useCallback(
    (itemId: number) => {
      const item = activeItemsRef.current.get(itemId);
      if (!item) return;
      // keep scoreRef in sync immediately, so finishRound never reads a stale value
      scoreRef.current += item.points;
      setScore(scoreRef.current);
      setLastPoints(item.points);
      setPointsFlashKey((key) => key + 1);
      playPointsFeedbackSound(item.points);
      if (feedbackTimerRef.current !== null) window.clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = window.setTimeout(() => {
        setLastPoints(null);
        feedbackTimerRef.current = null;
      }, POINTS_FEEDBACK_DURATION_MS);
      activeItemsRef.current.delete(itemId);
    },
    [playPointsFeedbackSound],
  );

  const startGame = (playerName: string) => {
    setName(playerName);
    scoreRef.current = 0;
    setScore(0);
    timeLeftRef.current = ROUND_SECONDS;
    setTimeLeft(ROUND_SECONDS);
    setLastPoints(null);
    setRoundOver(false);
    setCountdown(COUNTDOWN_FROM);
    setPhase("countdown");
  };

  const beginRound = useCallback(() => {
    itemIdRef.current = 0;
    spawnAccumulatorRef.current = 0;
    lastTimestampRef.current = null;
    activeItemsRef.current.clear();
    syncCanvasSize();
    drawCanvasFrame();
    setPhase("playing");
  }, [drawCanvasFrame, syncCanvasSize]);

  const finishRound = useCallback(() => {
    setRoundOver(true);
    clearActiveItems();
    const entry = { name, value: scoreRef.current, at: Date.now() };
    setEntries((previous) => {
      const updated = [...previous, entry];
      saveEntries(RANKING_KEY, updated);
      return updated;
    });
    endTimeoutRef.current = window.setTimeout(() => {
      endTimeoutRef.current = null;
      setRoundOver(false);
      setPhase("finished");
    }, END_PAUSE_MS);
  }, [clearActiveItems, name]);

  useEffect(() => {
    if (phase !== "countdown") return;
    if (countdown === 0) {
      beginRound();
      return;
    }
    const timeoutId = window.setTimeout(() => setCountdown((n) => n - 1), 1000);
    return () => window.clearTimeout(timeoutId);
  }, [beginRound, countdown, phase]);

  useEffect(() => {
    if (!arenaVisible) return;
    const handleResize = () => {
      syncCanvasSize();
      drawCanvasFrame();
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [arenaVisible, drawCanvasFrame, syncCanvasSize]);

  // Registered with { passive: false } (not React's onPointerDown) so preventDefault works and
  // no passive-listener warnings fire; the game is full-screen so scroll isn't needed.
  const handleArenaPointerDown = useCallback(
    (event: PointerEvent) => {
      if (!isRunning) return;
      const arenaElement = arenaRef.current;
      if (!arenaElement) return;
      let bounds = arenaBoundsRef.current;
      if (!bounds) {
        bounds = arenaElement.getBoundingClientRect();
        arenaBoundsRef.current = bounds;
      }
      pendingPointerRef.current = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
      event.preventDefault();
    },
    [isRunning],
  );

  useEffect(() => {
    const arenaElement = arenaRef.current;
    if (!arenaElement) return;
    arenaElement.addEventListener("pointerdown", handleArenaPointerDown, { passive: false });
    return () => arenaElement.removeEventListener("pointerdown", handleArenaPointerDown);
  }, [arenaVisible, handleArenaPointerDown]);

  // Reads and writes timeLeftRef instead of finishing inside a setState updater, which
  // StrictMode runs twice in development and would save the result twice.
  useEffect(() => {
    if (!isRunning) return;
    const timer = window.setInterval(() => {
      timeLeftRef.current -= 1;
      setTimeLeft(timeLeftRef.current);
      if (timeLeftRef.current <= 0) {
        window.clearInterval(timer);
        finishRound();
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [finishRound, isRunning]);

  useEffect(() => {
    if (!isRunning) {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      return;
    }

    const spawnIntervalMs = 1000 / ITEMS_PER_SECOND;

    const tick = (timestamp: number) => {
      if (lastTimestampRef.current === null) {
        lastTimestampRef.current = timestamp;
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      const deltaMs = Math.min(timestamp - lastTimestampRef.current, MAX_DELTA_MS);
      lastTimestampRef.current = timestamp;
      spawnAccumulatorRef.current += deltaMs;
      const dueSpawns = Math.floor(spawnAccumulatorRef.current / spawnIntervalMs);
      if (dueSpawns > 0) spawnAccumulatorRef.current -= dueSpawns * spawnIntervalMs;

      const { width: arenaWidth, height: arenaHeight, itemSize } = arenaMetricsRef.current;

      const pendingPointer = pendingPointerRef.current;
      if (pendingPointer) {
        pendingPointerRef.current = null;
        // perf: break on first hit instead of scanning every item (only runs on a tap)
        for (const item of Array.from(activeItemsRef.current.values())) {
          const left = (item.xPercent / 100) * arenaWidth - itemSize / 2;
          if (
            pendingPointer.x >= left &&
            pendingPointer.x <= left + itemSize &&
            pendingPointer.y >= item.y &&
            pendingPointer.y <= item.y + itemSize
          ) {
            handleItemHit(item.id);
            break;
          }
        }
      }

      const removedItemIds = removedItemBufferRef.current;
      removedItemIds.length = 0;
      // perf: hoist loop invariants; final-rush speed and deltaSeconds are the same for every item in a frame
      const speedMultiplier = timeLeftRef.current <= FINAL_RUSH_SECONDS ? FINAL_RUSH_MULTIPLIER : 1;
      const deltaSeconds = deltaMs / 1000;
      const offscreenThreshold = arenaHeight + 120;
      activeItemsRef.current.forEach((item) => {
        item.y += item.speed * speedMultiplier * deltaSeconds;
        if (item.y >= offscreenThreshold) removedItemIds.push(item.id);
      });
      for (const itemId of removedItemIds) activeItemsRef.current.delete(itemId);

      const availableSlots = Math.max(
        0,
        getDynamicMaxActiveItems(arenaWidth, arenaHeight) - activeItemsRef.current.size,
      );
      const spawnCount = Math.min(dueSpawns, MAX_SPAWNS_PER_FRAME, availableSlots);
      for (let index = 0; index < spawnCount; index += 1) {
        const nextItem = buildSpawnItem();
        if (nextItem) activeItemsRef.current.set(nextItem.id, nextItem);
      }

      drawCanvasFrame();
      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      lastTimestampRef.current = null;
    };
  }, [buildSpawnItem, drawCanvasFrame, handleItemHit, isRunning]);

  useEffect(() => {
    // capture refs at effect-creation time so the cleanup closure doesn't follow mutations across remounts
    const spriteCache = spriteCacheRef.current;
    const scaledSpriteCache = scaledSpriteCacheRef.current;
    return () => {
      feedbackSoundTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      feedbackSoundTimeoutsRef.current = [];
      if (feedbackTimerRef.current !== null) window.clearTimeout(feedbackTimerRef.current);
      if (endTimeoutRef.current !== null) window.clearTimeout(endTimeoutRef.current);
      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
      // perf: release pinned decoded-bitmap buffers; HTMLCanvasElement entries GC normally
      spriteCache.forEach((bitmap) => bitmap.close());
      spriteCache.clear();
      scaledSpriteCache.clear();
    };
  }, []);

  const clearRanking = () => {
    saveEntries(RANKING_KEY, []);
    setEntries([]);
  };

  if (!isTablet()) {
    return (
      <div className="unsupported-device">
        <button className="back-button" onClick={onBackToHome} title="Voltar ao início">
          ← Voltar
        </button>
        Este jogo está disponível apenas em tablets.
        <br />
        Aceda através de um dispositivo compatível ou utilize a emulação de dispositivo no
        navegador.
      </div>
    );
  }

  return (
    <div className="ad-event health-rush">
      {/* Only on registration, like the other games' start screens, so players can't leave mid-round. */}
      {phase === "register" ? (
        <button className="back-button" onClick={onBackToHome} title="Voltar ao início">
          ← Voltar
        </button>
      ) : null}

      <EventHeader adminEnabled={phase === "register"} onAdmin={() => setAdminOpen(true)} />

      {phase === "register" ? (
        <NameRegistration entries={entries} onStart={startGame} />
      ) : arenaVisible ? (
        <main className="ad-screen hr-playing">
          <section className="hr-arena-wrap">
            <h1 className="ad-banner hr-banner">Health Rush</h1>
            <div className="hr-arena" ref={arenaRef}>
              <img src={`${SPRITES}/virus-desfocado-1.webp`} alt="" className="hr-deco one" />
              <img src={`${SPRITES}/virus-desfocado-2.webp`} alt="" className="hr-deco two" />
              <canvas className="hr-canvas" ref={canvasRef} />
              {phase === "countdown" ? (
                <div className="hr-overlay hr-countdown" aria-live="assertive">
                  {countdown > 0 ? countdown : ""}
                </div>
              ) : roundOver ? (
                <div className="hr-overlay hr-time-up">Fim do tempo!</div>
              ) : null}
            </div>
          </section>
          <div className="hr-hud">
            <span>Pontos {score}</span>
            {/* Always rendered so "Pontos" and "Tempo" don't shift on every hit. */}
            <span
              key={pointsFlashKey}
              className={`hr-flash ${lastPoints === null ? "" : lastPoints > 0 ? "plus" : "minus"}`}>
              {lastPoints === null ? "" : lastPoints > 0 ? `+${lastPoints}` : lastPoints}
            </span>
            <span>Tempo {timeLeft}s</span>
          </div>
          <div className="ad-promo">
            <h2>O próximo desafio está noutro totem!</h2>
            <p>
              Testa os teus reflexos no SLIDING PUZZLE BORDADO
              <br />
              ALBICASTRENSE e tenta chegar ao topo do ranking.
            </p>
          </div>
        </main>
      ) : (
        <Podium
          ranked={rankEntries(entries, "desc")}
          playerName={name}
          format={(points) => `${points} pts`}
          onRestart={() => setPhase("register")}
        />
      )}

      {adminOpen ? (
        <AdminModal onClear={clearRanking} onClose={() => setAdminOpen(false)} />
      ) : null}
    </div>
  );
}
